import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import ReactTooltip from 'react-tooltip';
import { Toggle, Color } from '../inputControls';

export default function Section({
  customClass = '',
  config: { label, color, text, position, image, toggle, index, code, select, remove, number, dropdown, description, list, input } = {},
  children,
}) {
  const [numberValue, setNumberValue] = useState(number?.value || 0);
  const onChangeNumber = ({ currentTarget: { value: newValue } }) => setNumberValue(newValue);
  const [inputValue, setInputValue] = useState(input?.value || '');
  const onChangeInput = ({ currentTarget: { value: newValue } }) => setInputValue(newValue || '');

  useEffect(() => {
    if (number && number?.value) {
      setNumberValue(number?.value);
    }

    if (input && input?.value) {
      setInputValue(input?.value || '');
    }
  }, [number, input]);

  const isBannerImageFirstSection = label === 'Banner Image' && toggle && toggle.checked;

  return (
    <div className={`layout-section ${customClass}${isBannerImageFirstSection ? ' env-a-border-bottom-none env-a-p-bottom-none' : ''}`}>
      <div className={`layout-section-label${isBannerImageFirstSection ? ' env-a-m-bottom-none' : ''}`}>
        <span className="layout-section-label-content">
          {`${label}${['Sub Content', 'Intro Content'].includes(label) ? ` ${index + 1}` : ''}`}
        </span>
        {position && (
          <button type="button" data-tip={position.dataTip} onClick={position.onClick}>
            <FontAwesomeIcon icon={['fal', `arrow-circle-${position.icon}`]} />
          </button>
        )}
        {image && (
          <button type="button" data-tip={image.dataTip} onClick={image.onClick} className={image.className}>
            <FontAwesomeIcon icon={['fal', 'upload']} />
          </button>
        )}
        {number && (
          <input
            type="number"
            step="1"
            value={numberValue}
            onChange={onChangeNumber}
            onBlur={number.onChange}
            style={number.style}
            className={number.className}
            {...(label === 'Set Mobile Banner Height' ? { max: 1000, min: 0 } : {})}
          />
        )}
        {input && (
          <input
            value={inputValue}
            onChange={onChangeInput}
            onBlur={input.onChange}
            style={input.style}
            placeholder={input.placeholder}
            className={input.className}
          />
        )}
        {text && (
          <button onClick={text.onClick} type="button" data-tip={text.dataTip}>
            <FontAwesomeIcon icon={['fal', 'edit']} />
          </button>
        )}
        {dropdown && (
          <Select
            className={dropdown.className}
            onChange={dropdown.onChange}
            value={dropdown.value}
            options={dropdown.options}
            styles={dropdown.styles}
            isClearable={dropdown.isClearable}
            isSearchable={dropdown.isSearchable}
            isMulti={dropdown.isMulti}
            placeHolder={dropdown.placeHolder}
          />
        )}
        {select && (
          <select className={select.className} onChange={select.onChange} value={select.value}>
            {select.options.map(({ label: optionLabel, value }, optionIndex) =>
              optionLabel ? (
                <option key={['option', optionIndex, value].join('-')} value={value}>
                  {optionLabel}
                </option>
              ) : (
                <hr key={['option', optionIndex, value].join('-')} />
              ),
            )}
          </select>
        )}
        {color && (
          <Color
            onChange={color.onChange}
            color={color.color}
            position={color.position}
            tooltip={color.tooltip}
            tooltipPosition={color.tooltipPosition}
            disableAlpha={color.disableAlpha}
            alphaOnly={color.alphaOnly}
          />
        )}
        {code && (
          <button type="button" data-tip={code.dataTip} onClick={code.onClick}>
            <FontAwesomeIcon icon={['fal', 'edit']} />
          </button>
        )}
        {list && (
          <button type="button" data-tip={list.dataTip} onClick={list.onClick}>
            <FontAwesomeIcon icon={['fal', 'edit']} />
          </button>
        )}
        {toggle && <Toggle onChange={toggle.onChange} checked={toggle.checked} dataTip={toggle.dataTip || ''} />}
        {remove && (
          <button type="button" onClick={remove.onClick} data-tip={remove.dataTip}>
            <FontAwesomeIcon icon={['fal', 'trash']} />
          </button>
        )}
        <ReactTooltip />
      </div>
      <div className="layout-section-content">{children}</div>
      {description && <div className="layout-section-warning">{description}</div>}
    </div>
  );
}

Section.propTypes = {
  customClass: PropTypes.string,
  config: PropTypes.shape({
    label: PropTypes.string.isRequired,
    number: PropTypes.shape({
      value: PropTypes.number.isRequired,
      onChange: PropTypes.func.isRequired,
      style: PropTypes.shape({}),
      className: PropTypes.string,
    }),
    input: PropTypes.shape({
      value: PropTypes.string,
      placeholder: PropTypes.string,
      onChange: PropTypes.func.isRequired,
      style: PropTypes.shape({}),
      className: PropTypes.string,
    }),
    code: PropTypes.shape({
      dataTip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    }),
    remove: PropTypes.shape({
      onClick: PropTypes.func.isRequired,
      dataTip: PropTypes.string,
    }),
    image: PropTypes.shape({
      onClick: PropTypes.func.isRequired,
      dataTip: PropTypes.string,
      className: PropTypes.string,
    }),
    text: PropTypes.shape({
      dataTip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    }),
    dropdown: PropTypes.shape({
      className: PropTypes.string,
      onChange: PropTypes.func.isRequired,
      value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]).isRequired,
      options: PropTypes.arrayOf(PropTypes.object),
      styles: PropTypes.shape({}),
      isClearable: PropTypes.bool.isRequired,
      isSearchable: PropTypes.bool.isRequired,
      isMulti: PropTypes.bool,
      placeHolder: PropTypes.string,
    }),
    select: PropTypes.shape({
      className: PropTypes.string,
      onChange: PropTypes.func.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      options: PropTypes.arrayOf(PropTypes.object),
    }),
    color: PropTypes.shape({
      onChange: PropTypes.func.isRequired,
      color: PropTypes.string.isRequired,
      position: PropTypes.string,
      tooltip: PropTypes.string,
      tooltipPosition: PropTypes.string,
    }),
    toggle: PropTypes.shape({
      onChange: PropTypes.func.isRequired,
      checked: PropTypes.bool.isRequired,
      dataTip: PropTypes.string,
    }),
    position: PropTypes.shape({
      dataTip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.string.isRequired,
    }),
    index: PropTypes.number,
    button: PropTypes.shape({}),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    list: PropTypes.shape({
      dataTip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    }),
  }),
  children: PropTypes.node,
};
